import {CoreObjectArchiveData} from 'app-core/platform/archivable/CoreObjectArchiveConstants';
import {assert} from 'Asserts';
import {
  DilutionAssignmentType,
  ProcedureEdgeType,
  ProcedureParameterConfirmationMethod,
  ProcedureParameterUsageValueType,
  ProcedureType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {DocumentVersionUpdate} from 'notebook/documents/DocumentConstants';
import {
  BioentityLinkSchemaFieldTypedValue,
  DatetimeSchemaFieldTypedValue,
  FloatSchemaFieldTypedValue,
  TextSchemaFieldTypedValue,
} from 'notebook/tables/typed-values/TypedValueTypes';
import {
  DataParameterType,
  EquipmentType,
  MaterialOutputType,
  MaterialType,
  UnitSummary,
} from 'procedures/shared-components/schema-field-type-selectors/procedureSchemaFieldConstants';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {PublishingRecordForVersionHistoryPanel} from 'template-collectibles/publishing-ui/graphql/types';
import {capitalize} from 'util/NameUtil';

import {ProcedureGraphQL} from '../../graphql-fragments/procedures/__generated/ProcedureGraphQL';
import {ProcedureAnalysisTemplateMappings} from './design/analysis-template-config/AnalysisTemplateConfigTypes';

export {ProcedureEdgeType};

type ProcedurePermissions = ProcedureGraphQL['permissions'];

export type ParameterTypedValue = MaterialInputTypedValue | DataParameterTypedValue | EquipmentTypedValue;
export type DataParameterTypedValue =
  | TextSchemaFieldTypedValue
  | FloatSchemaFieldTypedValue
  | DatetimeSchemaFieldTypedValue;

export type MaterialInputAmountTypedValue = FloatSchemaFieldTypedValue;
export type MaterialInputTypedValue = TextSchemaFieldTypedValue | BioentityLinkSchemaFieldTypedValue;

/**
 * This type is used to allow downstream components to know about material flows. Specifically,
 * it enables config tables to represent a material which has been mapped, and therefore should
 * not be user-configurable in the table.
 *
 * This type is not persisted.
 */
export interface MaterialFlow {
  sourceMaterialCid: string;
  sourceMaterialName: string;
  sourceMethodName: string;
  targetMaterialCid: string;
  targetMaterialName: string;
  targetMethodName: string;
}

export type MethodComponent =
  | ProcedureDataParameterSetting
  | ProcedureMaterialInputSetting
  | ProcedureMaterialOutputSetting
  | ProcedureEquipmentSetting
  | ProcedureTestDefinition;

function isDataParameterSettingComponent(mc: MethodComponent): mc is ProcedureDataParameterSetting {
  return mc.hasOwnProperty('dataParameterVersion');
}

function isMaterialInputSettingComponent(mc: MethodComponent): mc is ProcedureMaterialInputSetting {
  return mc.hasOwnProperty('materialInputVersion');
}

export function isMaterialOutputSettingComponent(mc: MethodComponent): mc is ProcedureMaterialOutputSetting {
  return mc.hasOwnProperty('materialOutputVersion');
}

export function isEquipmentSettingComponent(mc: MethodComponent): mc is ProcedureEquipmentSetting {
  return mc.hasOwnProperty('equipmentVersion');
}

export function isTestDefinitionComponent(mc: MethodComponent): mc is ProcedureTestDefinition {
  return mc.hasOwnProperty('resultSchemaName');
}

export function isInaccessibleAnalysisTemplateConfiguration(
  at: ProcedureAnalysisTemplateConfiguration | InaccessibleProcedureAnalysisTemplateConfiguration
): at is InaccessibleProcedureAnalysisTemplateConfiguration {
  return (at as InaccessibleProcedureAnalysisTemplateConfiguration).invalidAnalysisTemplateId != null;
}

export function isInaccessibleProcedureTestDefinition(
  td: ProcedureTestDefinition | InaccessibleProcedureTestDefinition
): td is InaccessibleProcedureTestDefinition {
  return (td as InaccessibleProcedureTestDefinition).invalidId != null;
}

export interface ProcedureTestDefinition {
  id: string | null;
  name: string;
  description: string;
  resultSchemaName: string; // Will show (No access) if the result schema is inaccessible
}

export interface InaccessibleProcedureTestDefinition {
  invalidId: string;
}

export function getMethodComponentUnitString(obj: MethodComponent): string {
  if (isDataParameterSettingComponent(obj)) {
    return 'Parameter';
  } else if (isMaterialInputSettingComponent(obj)) {
    return 'Material input';
  } else if (isMaterialOutputSettingComponent(obj)) {
    return 'Material output';
  } else if (isEquipmentSettingComponent(obj)) {
    return 'Equipment';
  }
  assert(false, 'Unknown parameter setting');
}

export function getMethodComponentCid(obj: MethodComponent): string {
  if (isDataParameterSettingComponent(obj)) {
    return obj.dataParameterVersion.cid;
  } else if (isMaterialInputSettingComponent(obj)) {
    return obj.materialInputVersion.cid;
  } else if (isMaterialOutputSettingComponent(obj)) {
    return obj.materialOutputVersion.cid;
  } else if (isEquipmentSettingComponent(obj)) {
    return obj.equipmentVersion.cid;
  } else if (isTestDefinitionComponent(obj) && obj.id != null) {
    // Test definitions are not versioned, so we use the id as the cid
    return obj.id;
  }

  assert(false, 'Unknown parameter setting');
}

export function getMethodComponentName(obj: MethodComponent): string {
  if (isDataParameterSettingComponent(obj)) {
    return obj.dataParameterVersion.name;
  } else if (isMaterialInputSettingComponent(obj)) {
    return obj.materialInputVersion.name;
  } else if (isMaterialOutputSettingComponent(obj)) {
    return obj.materialOutputVersion.name;
  } else if (isEquipmentSettingComponent(obj)) {
    return obj.equipmentVersion.name;
  } else if (isTestDefinitionComponent(obj)) {
    return obj.name;
  }
  assert(false, 'Unknown parameter setting');
}

export interface ProcedureStepConfig {
  id: string | null; // Null for new steps, populated for existing steps
  cid: string; // Stable client identifier for all steps
  name: string;
  documentId: string | null;
  documentVersionUpdate: DocumentVersionUpdate | null;
  fromId: string | null; // Null for existing steps, populated for new steps created from existing model
}

export enum ProcedureStepGroupType {
  PRE_RUN = 'PRE_RUN',
  RUN = 'RUN',
  POST_RUN = 'POST_RUN',
}

export const PROCEDURE_STEP_GROUP_TYPE_TO_LABEL: Record<ProcedureStepGroupType, string> = {
  [ProcedureStepGroupType.PRE_RUN]: 'Pre-run',
  [ProcedureStepGroupType.RUN]: 'Run',
  [ProcedureStepGroupType.POST_RUN]: 'Post-run',
};

export function getStepGroupFromLabel(groupLabel: string): ProcedureStepGroupType {
  // We capitalize to standardize with `_step_group_to_name`, which uses different capitalization
  const standardizedLabel = capitalize(groupLabel);
  const [group] = Object.keys(PROCEDURE_STEP_GROUP_TYPE_TO_LABEL).filter(
    (key: ProcedureStepGroupType) => PROCEDURE_STEP_GROUP_TYPE_TO_LABEL[key] === standardizedLabel
  );
  return ProcedureStepGroupType[group as keyof typeof ProcedureStepGroupType];
}

export interface ProcedureStepGroup {
  group: ProcedureStepGroupType;
  steps: Array<ProcedureStepConfig>;
}

export interface ProcedureParameterVersion {
  id: string | null; // Null for new parameter versions, populated for existing ones
  cid: string; // Stable client identifier for parameter versions
  name: string;
  description: string;
  /** An extra field in case the parameter was cloned from another value. This is only sent up to the server
   * when cloning and is not a persisted value. */
  clonedFromId?: string | null;
}

/**
 * The versioned procedure parameter object (ProcedureDataParameterVersion in DG).
 */
export interface ProcedureDataParameterVersion extends ProcedureParameterVersion {
  dataType: DataParameterType;
  minimum: FloatSchemaFieldTypedValue | null; // null only used for non-numeric dataType. "empty" typedvalue otherwise.
  maximum: FloatSchemaFieldTypedValue | null; // null only used for non-numeric dataType. "empty" typedvalue otherwise.
  confirmationMethod: ProcedureParameterConfirmationMethod;
  /** The schema field that represents the parameter's value. This is needed for interactions with StructuredTables. */
  restValueField?: SchemaField;
  /** procedureDataParameterId is the unversioned procedure parameter object (ProcedureDataParameter in DG). */
  procedureDataParameterId: string | null;
}

export interface ProcedureDataParameterPlannedValue {
  id: string | null;
  cid: string; // Stable client identifier for parameter planned values
  value: DataParameterTypedValue;
}

export type ProcedureParameterSetting =
  | ProcedureDataParameterSetting
  | ProcedureMaterialInputSetting
  | ProcedureMaterialOutputSetting
  | ProcedureEquipmentSetting;

export interface ProcedureDataParameterSetting {
  dataParameterVersion: ProcedureDataParameterVersion;
  values: Array<ProcedureDataParameterPlannedValue>;
}

export interface ProcedureMaterialInputSetting {
  materialInputVersion: ProcedureMaterialInputVersion;
  values: Array<ProcedureMaterialInputPlannedValue>;
}

export interface ProcedureMaterialOutputSetting {
  materialOutputVersion: ProcedureMaterialOutputVersion;
}

/**
 * The versioned procedure material input object (ProcedureMaterialInputVersion in DG).
 */
export interface ProcedureMaterialInputVersion extends ProcedureParameterVersion {
  dataType: MaterialType;
  amountUnit: UnitSummary | null;
  confirmationMethod: ProcedureParameterConfirmationMethod;
  /** The schema field that represents the material. This is needed for interactions with StructuredTables. */
  restMaterialField?: SchemaField;
  /** The schema field that represents the amount. This is needed for interactions with StructuredTables. */
  restAmountField?: SchemaField;
  /** procedureMaterialInputId is the unversioned procedure material input object (ProcedureMaterialInput in DG). */
  procedureMaterialInputId: string | null;
}

/**
 * The versioned procedure material output object (ProcedureMaterialOutputVersion in DG).
 */
export interface ProcedureMaterialOutputVersion extends ProcedureParameterVersion {
  dataType: MaterialOutputType;
  /** procedureMaterialOutputId is the unversioned procedure material output object (ProcedureMaterialOutput in DG). */
  procedureMaterialOutputId: string | null;
  /** The schema field that represents the material. This is needed for interactions with StructuredTables. */
  restMaterialField?: SchemaField;
}

export type ProcedureParameterDataTypeDefinition =
  | MaterialOutputType
  | MaterialType
  | EquipmentType
  | DataParameterType;

export interface ProcedureMaterialInputPlannedValue {
  id: string | null;
  cid: string; // Stable client identifier for material input planned values
  material: MaterialInputTypedValue;
  amount: MaterialInputAmountTypedValue | null;
}

export interface ProcedureEquipmentSetting {
  equipmentVersion: ProcedureEquipmentVersion;
  values: Array<ProcedureEquipmentPlannedValue>;
}

export type EquipmentTypedValue = TextSchemaFieldTypedValue | BioentityLinkSchemaFieldTypedValue;
export type EquipmentTypedModel = TextSchemaFieldTypedValue | BioentityLinkSchemaFieldTypedValue;

/**
 * The versioned procedure equipment object (ProcedureEquipmentVersion in DG).
 */
export interface ProcedureEquipmentVersion extends ProcedureParameterVersion {
  dataType: EquipmentType;
  models: EquipmentTypedModel;
  /** procedureEquipmentId is the unversioned procedure equipment object (ProcedureEquipment in DG). */
  procedureEquipmentId: string | null;
  /** The schema field that represents the equipment. This is needed for interactions with StructuredTables. */
  restEquipmentField?: SchemaField;
}

export interface ProcedureEquipmentPlannedValue {
  id: string | null;
  cid: string; // Stable client identifier for equipment planned values
  equipment: EquipmentTypedValue;
}

export interface ProcedureAnalysisTemplateConfiguration {
  analysisTemplateId: string;
  config: ProcedureAnalysisTemplateMappings;
}

export interface InaccessibleProcedureAnalysisTemplateConfiguration {
  invalidAnalysisTemplateId: string;
}

export interface DilutionPlan {
  allowedDilutionFactors: Array<number>;
  dilutionAssignment: DilutionAssignmentType;
}

export interface Method {
  // Backend API identifier. Will be null for new methods, on save the backend returns the proper ids
  id: string | null;
  /**
   * Session-stable identifier for method nodes. Works across versions and before this method is first saved.
   */
  cid: string;
  name: string;
  description: string;
  /**
   * dataParameterSettings is the schema-backed representation (ProcedureDataParameterSetting in DG)
   */
  dataParameterSettings: Array<ProcedureDataParameterSetting>;
  materialInputSettings: Array<ProcedureMaterialInputSetting>;
  materialOutputSettings: Array<ProcedureMaterialOutputSetting>;
  equipmentSettings: Array<ProcedureEquipmentSetting>;
  parameterUsages: ParameterUsages;
  stepGroups: Array<ProcedureStepGroup>;
  /**
   * The method version from a different flowchart that was cloned to create this method
   *  and all of its iterations. Is null for all new methods.
   *  When duplicating within a procedure, is duplicatingMethod.sourceMethodId.
   *  When duplicating into a new procedure, is duplicatingMethod.id
   */
  sourceMethodId: string | null;
  /**
   * A unique identifier for a method and all of its iterations within a procedure.
   * A new ID is generated for new methods and cloned methods
   * The same ID is used when "updating" a method
   */
  nodeId: string;
  // The originating method in a method lineage receives a unique provenance ID. Spans across flowcharts.
  provenanceId: string;
  /**
   * Analysis templates & corresponding configuration information associated with the method. Includes both
   * accessible & inaccessible templates, though the presence of the latter will result in the procedure
   * being locked.
   */
  analysisTemplateConfigurations: Array<
    ProcedureAnalysisTemplateConfiguration | InaccessibleProcedureAnalysisTemplateConfiguration
  >;
  /**
   * The test definitions associated with this method if the procedure is assay-typed
   */
  testDefinitions: Array<ProcedureTestDefinition | InaccessibleProcedureTestDefinition>;
  /**
   * The dilution plan for this method. Contains a list of allowed dilution factors for the method as well as
   * dilution assignment type.
   */
  dilutionPlan: DilutionPlan;
}

export const enum ProcedureNodeType {
  METHOD = 'METHOD',
}

export interface CanvasGridCoordinates {
  row: number;
  column: number;
}

interface AnyProcedureNode {
  canvasGridCoordinates: CanvasGridCoordinates;
}

export interface MethodNode extends Method, AnyProcedureNode {
  type: ProcedureNodeType.METHOD;
}

interface BaseEdge {
  id: string; // Not persisted, computed on frontend for flowchart editor purposes
  fromMethodCid: string;
  toMethodCid: string;
  sourceHandle: string;
  targetHandle: string;
}

export interface MethodEdge extends BaseEdge {
  type: ProcedureEdgeType.METHOD_EDGE;
}

export interface MaterialEdge extends BaseEdge {
  type: ProcedureEdgeType.MATERIAL_EDGE;
}

export type ProcedureEdge = MethodEdge | MaterialEdge;

export interface ProcedureFlowchart {
  nodes: Array<MethodNode>;
  edges: Array<ProcedureEdge>;
}

// This is derived from the ownerJson GQL shape, which contains a superset of entity.to_summary_dict()
export interface ProcedureTemplateCollectionOwner {
  id: string;
  name: string;
  type: string;
}

export interface ProcedureMetadata {
  id: string;
  name: string;
  templateCollectionId: string;
  procedureType: ProcedureType;
  archiveData: CoreObjectArchiveData;
  creator: ProcedureGraphQL['creator'];
  createdAt: string;
  modifiedAt: string;
  permissions: ProcedurePermissions;
  templateCollectionOwner: ProcedureTemplateCollectionOwner;
  publishingRecords: Array<PublishingRecordForVersionHistoryPanel>;
}

export interface ProcedureWithMetadata {
  flowchart: ProcedureFlowchart;
  metadata: ProcedureMetadata;
  // Will be required after migrating all procedure configs into workflows orchestration
  processFlowchartConfigId: string | null;
}

export interface ProcedureDocumentVersionUpdate extends DocumentVersionUpdate {
  documentId: string;
}

export interface ParameterUsage {
  parameterVersionId: string;
  valueType: ProcedureParameterUsageValueType;
  stepId: string;
  stepCid: string;
  stepName: string;
}

// Keep in sync with ProcedureParameterType Domain Graph type
export enum ProcedureParameterType {
  DATA_PARAMETER = 'DATA_PARAMETER',
  MATERIAL_INPUT = 'MATERIAL_INPUT',
  EQUIPMENT = 'EQUIPMENT',
  MATERIAL_OUTPUT = 'MATERIAL_OUTPUT',
}

/** A map of ParameterUsages grouped by ParameterVersionId -> Usages */
export type ParameterUsages = Record<string, Array<ParameterUsage> | undefined>;
